// =============================================================================
// eodTodayShape — the PURE daily_reports row -> eodToday shape mapper.
// No network, no RLS, no staff lookup. Extracted from fetchEodToday
// (sellouts.js) so it can be unit-tested without a browser or Supabase.
//
// Given a `daily_reports` row and the staff name ALREADY resolved for it (see
// staffNameFor in sellouts.js), it produces the EXACT shape the sheet's
// getEodToday returns and the FOH End-of-Day screen reads:
//   { submitted, staffName, timestamp, date, waste, wasteByItem, tgtgSold, tgtgNotCollected }
//
// THE ONE FIX (10 Sep 2026, Lane AP). An EMPTY waste_by_item map must read as
// "" (falsy), NOT the string "{}" (truthy). The screen only reconstructs
// per-SKU waste from the human `waste` summary when wasteByItem is FALSY
// (foh.jsx ~L4555-4562). The sheet's getEodToday emits '' for a blank
// WasteByItem cell (String(cell||'')), so "" restores exact parity. Emitting
// "{}" — which is what JSON.stringify({}) produces — suppressed that fallback
// and hydrated every SKU to 0 for retro / backfilled rows (waste_by_item
// defaults to '{}' while waste_summary carries the real waste), and an
// undo-then-resubmit on that zeroed form wrote waste:"None" back into the sheet.
// See REVIEW_PACK_AP.md · Flip A.
//
// Behaviour is IDENTICAL to the old inline mapping in every case except an
// EMPTY-OR-NON-OBJECT waste_by_item (empty {}, null, [], a scalar) — which now
// emits "" instead of "{}"/"[]"/etc. In every one of those cases the screen's
// fallback would have re-fired anyway (its own `typeof !== "object"` guard) or
// was being wrongly suppressed by "{}"; so "" is never worse than before and is
// exact sheet-blank parity. LIVE submits are unaffected: recordEodSubmit
// (selloutSync.js) writes waste_by_item as a full {SKU:qty} map over every SKU
// (non-empty even on a zero-waste day), so Trafford/Arndale — live today — get a
// byte-identical JSON string. Only retro/backfill empty-map rows change.
// =============================================================================

// A DB date "YYYY-MM-DD" -> "DD/MM/YYYY" (EOD banner parity with the sheet feed).
export const ymdToUK = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(d || "");
};

// `resolvedName` is staff_name(submitted_by) or null — the caller resolves it
// only when it will actually be used (a retro row prefers the free-text worker
// name over the admin who logged it). The name selection below is preserved
// EXACTLY from the original fetchEodToday:
//   retro  -> original_staff_name, else the resolved name (only if submitted_by), else null
//   normal -> the resolved name (only if submitted_by), else original_staff_name, else null
export function eodTodayShape(row, resolvedName = null) {
  const staffName = row.status === "retro"
    ? (row.original_staff_name || (row.submitted_by ? resolvedName : null) || null)
    : (row.submitted_by ? (resolvedName ?? null) : (row.original_staff_name || null));

  // waste_by_item is a jsonb column, so supabase-js hands it back ALREADY PARSED
  // — a JS object ({} when empty), never the string "{}". The check is therefore
  // on the parsed object (keys > 0), not a "{}" string comparison. Anything that
  // is not a non-empty object — empty {}, null, undefined, an array, or (defensively)
  // a stringified value from some future double-encoded write path — falls to ""
  // and lets the screen's summary fallback run, which is always safe (never a silent
  // zeroing). Pinned by eodTodayShape.test.mjs.
  const map = row.waste_by_item;
  const hasWaste = map != null && typeof map === "object" && !Array.isArray(map) && Object.keys(map).length > 0;

  return {
    submitted: true,
    staffName,
    timestamp: row.submitted_at,
    date: ymdToUK(row.trading_date),
    waste: row.waste_summary || "None",
    // Sheet-blank parity: an empty map -> "" (falsy) so the screen's summary
    // fallback runs. A populated map -> its JSON string, exactly as before.
    wasteByItem: hasWaste ? JSON.stringify(map) : "",
    tgtgSold: row.tgtg_sold,
    tgtgNotCollected: row.tgtg_not_collected,
  };
}
